import { useCallback, useContext, useEffect, useState } from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';

import { getCookie } from '@atlassian/browser-storage-controls';

import { HorizontalNavContext } from '../components/horizontal-nav/horizontal-nav-context';
import urls from '../urls';

export const usePref: (
  prefKey: string,
  skipPrefCall: boolean
) => [string, (value: string) => undefined, boolean] = (
  prefKey,
  skipPrefCall
) => {
  const { currentUser } = useContext(HorizontalNavContext);
  const [isLoading, setIsLoading] = useState(true);
  const [prefValue, setPrefValue] = useState<string>('');

  useEffect(() => {
    if (!currentUser?.uuid || skipPrefCall) return undefined;

    fetch(
      urls.api.internal.userPreference(
        currentUser.uuid,
        encodeURIComponent(prefKey.toString())
      )
    )
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 404) {
            // 404s are returned when no value has been set for a user/pref combination
            return Promise.resolve('');
          }
        }
        return Promise.reject(
          new Error(`Error resolving user pref in global nav: ${prefKey}`)
        );
      })
      .then((val: string) => {
        setPrefValue(val);
      })
      .catch(error => {
        Sentry.captureException(error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return undefined;
  }, [currentUser?.uuid, prefKey, skipPrefCall]);

  const updatePref = useCallback(
    (value: string) => {
      if (!currentUser?.uuid) return undefined;
      const token = getCookie('csrftoken');

      setIsLoading(true);
      fetch(
        urls.api.internal.userPreference(
          currentUser.uuid,
          encodeURIComponent(prefKey.toString())
        ),
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': token || '',
          },
          body: JSON.stringify({ value }),
        }
      )
        .then(response => {
          if (response.ok) {
            return response.json();
          } else {
            return Promise.reject(
              new Error(`Error updating user pref in global nav: ${prefKey}`)
            );
          }
        })
        .then(response => {
          setPrefValue(response.value);
        })
        .catch(error => Sentry.captureException(error))
        .finally(() => {
          setIsLoading(false);
        });
      return undefined;
    },
    [currentUser?.uuid, prefKey]
  );

  return [prefValue, updatePref, isLoading];
};
